import type { OpenFeatureSupportedFlags } from '../../openfeature/types';
import type { FlagKey } from './types';

export const flags = {
  'pricing-social-proof': {
    enabled: [true, false],
  },
  'new-latest-results-widget': {
    enabled: [true, false],
  },
  'live-editor-csat': {
    enabled: [true, false],
  },
  'nux-editor-checklist': {
    enabled: [true, false],
  },
  'nux-business-training': {
    enabled: [true, false],
  },
  'nux-help-popover-in-editor': {
    enabled: [true, false],
  },
  'ins-reset-results-v2': {
    enabled: [true, false],
  },
  'ins-call-new-endpoints': {
    enabled: [true, false],
  },
  'ins-results-summary-cards': {
    enabled: [true, false],
  },
  'nux-interactive-quick-actions': {
    enabled: [true, false],
  },
  'ai-menti-builder': {
    version: ['v1', 'v2', 'v3', 'off'],
  },
  'mx-create-migrated-presentations': {
    enabled: [true, false],
  },
  'bees-editor-mobile-experience': {
    enabled: [true, false],
  },
  'bees-participant-rating': {
    enabled: [true, false],
  },
  'bees-screenshot-in-preview': {
    enabled: [true, false],
  },
  'bees-share-modal-redesign': {
    enabled: [true, false],
  },
  'og-homepage-personalized-header': {
    enabled: [true, false],
  },
  'og-google-signup': {
    version: ['v1', 'v2'],
  },
  'mangos-find-and-join-workspace': {
    enabled: [true, false],
  },
  'cx-hide-deprecated-slide-types': {
    enabled: [true, false],
  },
  'cx-inline-slide-recommendations': {
    enabled: [true, false],
  },
  'sli-informative-hidden-state': {
    enabled: [true, false],
  },
} as const satisfies OpenFeatureSupportedFlags;

export const flagsMetadata: Record<
  FlagKey,
  { description: string; displayName: string }
> = {
  'pricing-social-proof.enabled': {
    displayName: 'PUG011 - Social proof on plans page',
    description: 'Show client logos on the pricing page',
  },
  'new-latest-results-widget.enabled': {
    displayName: 'PUG012 - Increase adoption of Results from Home',
    description: 'Show a widget with extra results information',
  },
  'live-editor-csat.enabled': {
    displayName: 'LIVE - Show CSAT Form',
    description: 'Show the CSAT form to the user in the editor.',
  },
  'nux-editor-checklist.enabled': {
    displayName: 'NUX - Editor Checklist',
    description: 'Show a checklist in the editor',
  },
  'nux-business-training.enabled': {
    displayName: 'NUX - Personalized Business Training Quick Actions',
    description: 'Show Business Training Quick Actions in the NUX',
  },
  'ins-reset-results-v2.enabled': {
    displayName: 'INS - New way of resetting results',
    description: 'In dashboard, results page and editor',
  },
  'ins-call-new-endpoints.enabled': {
    displayName: 'INS - Call new endpoints',
    description: 'Call the new endpoints for results page',
  },
  'ins-results-summary-cards.enabled': {
    displayName: 'INS - Summary cards',
    description: 'Show summary cards in the Results page',
  },
  'nux-interactive-quick-actions.enabled': {
    displayName: 'NUX - Interactive Quick Actions',
    description: 'Show an additional interactive step in the onboarding',
  },
  'nux-help-popover-in-editor.enabled': {
    displayName: 'NUX - Help popover in the editor',
    description:
      'Automatically show the help modal for first time users in the editor',
  },
  'ai-menti-builder.version': {
    displayName: 'AI001 - AI Menti Builder',
    description: 'Show the AI Menti Builder button in the dashboard',
  },
  'mx-create-migrated-presentations.enabled': {
    displayName: 'MX - Create migrated presentations',
    description: 'Create migrated presentations',
  },
  'bees-editor-mobile-experience.enabled': {
    displayName: 'BEES - Improved mobile experience in the editor',
    description: 'Experiment with the mobile experience in the editor',
  },
  'bees-participant-rating.enabled': {
    displayName: 'BEES - Participant rating experiment',
    description: 'Experiment with rating on the end screen',
  },
  'bees-screenshot-in-preview.enabled': {
    displayName: 'BEES - Screenshot in Dashboard Preview',
    description: 'Experiment with screenshots in previews in the dashboard',
  },
  'bees-share-modal-redesign.enabled': {
    displayName: 'BEES - Share Modal Redesign',
    description: 'Roll out of new share modal design',
  },
  'og-homepage-personalized-header.enabled': {
    displayName: 'OG - Homepage Personalized Header',
    description: 'Show a personalized header on the homepage for executives',
  },
  'og-google-signup.version': {
    displayName: 'OG - Downprioritize Google Signup',
    description: 'Downprioritize Google Signup experiment in the signup flow',
  },
  'mangos-find-and-join-workspace.enabled': {
    displayName: 'MANGOS - Find and join workspace',
    description:
      'Allow new users to find and join a workspace in the onboarding process',
  },
  'cx-hide-deprecated-slide-types.enabled': {
    displayName: 'CX - Hide deprecated slide types',
    description: 'Hide deprecated slide types in the add slide popover',
  },
  'cx-inline-slide-recommendations.enabled': {
    displayName: 'CX - Inline slide recommendations',
    description: 'Enables slide recommendations based on slide deck use case',
  },
  'sli-informative-hidden-state.enabled': {
    displayName: 'SLIDES - Informative hidden state',
    description: 'Shows response rate when hiding results',
  },
};
